/**
 * Voxly — global incoming call listener + fullscreen ringing modal.
 *
 * Mounted ONCE inside AuthProvider so it's alive on every authed screen.
 * Polls `/api/dms/incoming-calls` every 4 s; when a fresh call_invite from
 * someone else lands in any of my conversations, we pop a fullscreen Accept
 * / Decline screen with the caller's avatar pulsing and a phone ringtone.
 *
 * Decline → marks `declined_by` server-side so the same invite never re-pops.
 * Accept  → routes to /call/{conv}?mode=...&action=join (existing CallPage).
 * The poll automatically stops when the user signs out.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, PhoneSlash, VideoCamera } from "@phosphor-icons/react";
import { api, avatarUrl } from "@/lib/api";
import { createRingtone } from "@/lib/ringtone";
import logger from "@/lib/logger";

const POLL_INTERVAL_MS = 4000;
const SEEN_KEY = "voxly:dismissed-call-msg";

export default function IncomingCallListener({ enabled }) {
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const ringRef = useRef(null);
  const pollingRef = useRef(false);

  const poll = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const { data } = await api.get("/dms/incoming-calls");
      const incoming = data?.call;
      if (incoming) {
        // Don't re-show a call the user just dismissed on this tab.
        try {
          const dismissed = sessionStorage.getItem(SEEN_KEY);
          if (dismissed === incoming.message_id) {
            return;
          }
        } catch {
          // sessionStorage disabled — show the modal again, still safe.
        }
        // If we're already inside that very call screen, don't pop the modal.
        if (typeof window !== "undefined" && window.location.pathname.startsWith(`/call/${incoming.conversation_id}`)) {
          return;
        }
        setCall((cur) => {
          // Don't churn state if the same call is still ringing.
          if (cur && cur.message_id === incoming.message_id) return cur;
          return incoming;
        });
      } else {
        // Caller stopped ringing (45 s timeout server-side or they hung up).
        setCall(null);
      }
    } catch (err) {
      // 401 is handled globally by the api interceptor; everything else we
      // log but don't toast — polling should be silent.
      logger.warn("incoming-call", "poll failed", err);
    } finally {
      pollingRef.current = false;
    }
  }, []);

  // Start / stop the polling loop based on auth state.
  useEffect(() => {
    if (!enabled) {
      setCall(null);
      return undefined;
    }
    let cancelled = false;
    const run = () => { if (!cancelled) poll(); };
    run();
    const id = setInterval(run, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, poll]);

  // Drive the ringtone purely from the presence of `call`.
  useEffect(() => {
    if (!call) {
      if (ringRef.current) { ringRef.current.stop(); ringRef.current = null; }
      return undefined;
    }
    const ring = createRingtone();
    ringRef.current = ring;
    ring.start();
    return () => {
      try { ring.stop(); } catch {
        // Already torn down — safe to ignore.
      }
      ringRef.current = null;
    };
  }, [call]);

  if (!enabled || !call) return null;

  const accept = () => {
    try { sessionStorage.setItem(SEEN_KEY, call.message_id); } catch {
      // Storage disabled — still proceed.
    }
    const c = call;
    setCall(null);
    navigate(`/call/${c.conversation_id}?mode=${encodeURIComponent(c.mode || "voice")}&action=join`);
  };

  const decline = async () => {
    try { sessionStorage.setItem(SEEN_KEY, call.message_id); } catch {
      // Storage disabled — still proceed.
    }
    const messageId = call.message_id;
    setCall(null);
    try {
      await api.post(`/dms/incoming-calls/${messageId}/decline`);
    } catch (err) {
      logger.warn("incoming-call", "decline failed", err);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-between py-12 px-6"
      data-testid="incoming-call-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Incoming call"
    >
      <div className="mt-10 text-center">
        <p className="text-sm font-mono uppercase tracking-[0.25em] text-white/60" data-testid="incoming-call-label">
          Voxly · Incoming {call.mode === "video" ? "video call" : "voice call"}
        </p>
        <p className="mt-2 text-xs text-white/40">Ringing…</p>
      </div>

      <div className="flex flex-col items-center gap-5">
        <div className="relative">
          <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping" aria-hidden />
          <img
            src={avatarUrl(call.caller?.picture)}
            alt=""
            className="relative h-36 w-36 rounded-full object-cover ring-4 ring-primary/60 shadow-2xl"
            data-testid="incoming-call-avatar"
          />
        </div>
        <div className="text-center">
          <p className="font-heading text-3xl font-black text-white" data-testid="incoming-call-name">
            {call.caller?.name || "Someone"}
          </p>
          {call.caller?.username && (
            <p className="mt-1 text-sm text-white/60">@{call.caller.username}</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-16 mb-4" data-testid="incoming-call-actions">
        <button
          type="button"
          onClick={decline}
          data-testid="incoming-call-decline"
          className="flex flex-col items-center gap-2 active:scale-95 transition"
          aria-label="Decline call"
        >
          <span className="h-16 w-16 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-2xl">
            <PhoneSlash size={28} weight="fill" />
          </span>
          <span className="text-xs text-white/80 font-bold uppercase tracking-wider">Decline</span>
        </button>
        <button
          type="button"
          onClick={accept}
          data-testid="incoming-call-accept"
          className="flex flex-col items-center gap-2 active:scale-95 transition"
          aria-label="Accept call"
        >
          <span className="h-16 w-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-2xl glow">
            {call.mode === "video" ? <VideoCamera size={28} weight="fill" /> : <Phone size={28} weight="fill" />}
          </span>
          <span className="text-xs text-white/80 font-bold uppercase tracking-wider">Accept</span>
        </button>
      </div>
    </div>
  );
}
